import { useState, useMemo } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import dayjs from 'dayjs';
import {
  ArrowLeft, MessageSquare, Send, Trash2, CalendarDays, FolderKanban, User, UserCheck,
  Clock, Tag, AlertTriangle, CheckCircle2, ListChecks, Save, Reply, X, CornerDownRight,
} from 'lucide-react';
import PageHeader from '@/components/PageHeader';
import { Card } from '@/components/Card';
import Modal from '@/components/Modal';
import { StatusBadge, PriorityBadge } from '@/components/Badge';
import Loader from '@/components/Loader';
import { taskService } from '@/services';
import { useAuth } from '@/hooks/useAuth';
import { fmtDate, fmtDateTime } from '@/utils/format';

export default function TaskDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { user, isAdmin } = useAuth();
  const [showDelete, setShowDelete] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['task', id],
    queryFn: () => taskService.get(id),
    refetchInterval: 10_000,
  });

  if (isLoading) return <Loader />;
  if (!data?.task) {
    return (
      <div className="text-center py-16">
        <div className="font-semibold text-lg">Task not found</div>
        <Link to="/tasks" className="text-brand-600 hover:underline text-sm">Back to tasks</Link>
      </div>
    );
  }

  const t = data.task;
  const isAssignee = t.assignee && String(t.assignee._id) === String(user?._id);
  const isCreator = t.createdBy && String(t.createdBy._id) === String(user?._id);
  const canEdit = isAdmin || isAssignee || isCreator;
  const canDelete = isAdmin || isCreator;

  const isOverdue = t.deadline && t.status !== 'completed' && dayjs(t.deadline).isBefore(dayjs(), 'day');

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3">
        <Link to="/tasks" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-brand-600">
          <ArrowLeft size={14} /> Back to tasks
        </Link>
        {canDelete && (
          <button
            onClick={() => setShowDelete(true)}
            className="inline-flex items-center gap-1.5 text-sm font-medium text-rose-600 hover:text-rose-700 hover:bg-rose-50 dark:hover:bg-rose-500/10 px-3 py-1.5 rounded-lg transition"
          >
            <Trash2 size={14} /> Delete task
          </button>
        )}
      </div>

      <PageHeader
        title={t.title}
        subtitle={t.project?.name && (
          <span className="inline-flex items-center gap-1">
            <FolderKanban size={12} />
            <Link to={`/projects/${t.project._id}`} className="hover:underline">{t.project.name}</Link>
          </span>
        )}
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <StatusBadge status={t.status} />
            <PriorityBadge priority={t.priority} />
            {isOverdue && (
              <span className="badge bg-rose-100 text-rose-700 dark:bg-rose-500/20 dark:text-rose-300">
                <AlertTriangle size={12} /> Overdue
              </span>
            )}
          </div>
        }
      />

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          <Card>
            <div className="font-semibold mb-2 flex items-center gap-2"><ListChecks size={16} /> Description</div>
            {t.description ? (
              <p className="text-sm text-slate-700 dark:text-slate-200 whitespace-pre-wrap leading-relaxed">{t.description}</p>
            ) : (
              <p className="text-sm text-slate-400 italic">No description provided.</p>
            )}

            <div className="mt-5">
              <div className="flex items-center justify-between mb-1">
                <div className="text-xs text-slate-500 font-medium">Completion</div>
                <div className="text-xs text-slate-500">{t.completionPercent || 0}%</div>
              </div>
              <div className="h-2 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                <div
                  className={`h-full ${
                    (t.completionPercent || 0) >= 100
                      ? 'bg-gradient-to-r from-emerald-500 to-teal-500'
                      : 'bg-gradient-to-r from-brand-500 to-indigo-500'
                  }`}
                  style={{ width: `${t.completionPercent || 0}%` }}
                />
              </div>
            </div>

            {(t.tags || []).length > 0 && (
              <div className="mt-4 flex flex-wrap gap-1.5">
                {t.tags.map((tag) => (
                  <span key={tag} className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300">
                    <Tag size={10} /> {tag}
                  </span>
                ))}
              </div>
            )}
          </Card>

          {canEdit && (
            <UpdatePanel
              task={t}
              onSaved={() => qc.invalidateQueries({ queryKey: ['task', id] })}
            />
          )}

          <CommentsPanel
            task={t}
            onChanged={() => qc.invalidateQueries({ queryKey: ['task', id] })}
          />
        </div>

        <div className="space-y-4">
          <Card>
            <div className="font-semibold mb-3">Details</div>
            <DetailRow icon={UserCheck} label="Assignee" value={t.assignee?.name || 'Unassigned'} sub={t.assignee?.email} />
            <DetailRow icon={User} label="Assigned by" value={t.createdBy?.name || '—'} sub={t.createdBy?.email} />
            <DetailRow icon={CalendarDays} label="Deadline" value={t.deadline ? fmtDate(t.deadline) : 'No deadline'}
              sub={t.deadline && (dayjs(t.deadline).diff(dayjs(), 'day') >= 0
                ? `in ${dayjs(t.deadline).diff(dayjs(), 'day')} day(s)`
                : `${dayjs().diff(t.deadline, 'day')} day(s) overdue`
              )}
            />
            <DetailRow icon={Clock} label="Estimated" value={`${t.estimatedHours || 0} h`} />
            <DetailRow icon={CheckCircle2} label="Completed at" value={t.completedAt ? fmtDateTime(t.completedAt) : '—'} />
            <DetailRow icon={Clock} label="Created" value={fmtDateTime(t.createdAt)} />
          </Card>

          {t.project && (
            <Card>
              <div className="font-semibold mb-2 flex items-center gap-2"><FolderKanban size={14} /> Project</div>
              <Link to={`/projects/${t.project._id}`} className="font-medium hover:underline">{t.project.name}</Link>
              {t.project.code && <div className="text-xs text-slate-500 mt-0.5">{t.project.code}</div>}
              <Link
                to={`/projects/${t.project._id}/chat`}
                className="mt-3 inline-flex items-center gap-1.5 text-sm text-brand-600 hover:underline"
              >
                <MessageSquare size={14} /> Open project chat
              </Link>
            </Card>
          )}
        </div>
      </div>

      {showDelete && (
        <DeleteTaskModal
          task={t}
          onClose={() => setShowDelete(false)}
          onDeleted={() => {
            qc.invalidateQueries({ queryKey: ['tasks'] });
            qc.removeQueries({ queryKey: ['task', id] });
            navigate('/tasks');
          }}
        />
      )}
    </div>
  );
}

function DetailRow({ icon: Icon, label, value, sub }) {
  return (
    <div className="flex items-start gap-3 py-2 border-b last:border-0 border-slate-100 dark:border-slate-800">
      <Icon size={14} className="text-slate-400 mt-0.5 shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="text-[11px] uppercase tracking-wide text-slate-500">{label}</div>
        <div className="text-sm font-medium truncate">{value}</div>
        {sub && <div className="text-[11px] text-slate-400 truncate">{sub}</div>}
      </div>
    </div>
  );
}

function UpdatePanel({ task, onSaved }) {
  const { register, handleSubmit, formState: { isSubmitting, isDirty }, reset } = useForm({
    defaultValues: {
      status: task.status,
      completionPercent: task.completionPercent || 0,
      deadline: task.deadline ? dayjs(task.deadline).format('YYYY-MM-DD') : '',
      priority: task.priority,
    },
  });

  const onSubmit = async (data) => {
    try {
      const payload = { ...data, completionPercent: Number(data.completionPercent) };
      if (!payload.deadline) payload.deadline = null;
      await taskService.update(task._id, payload);
      toast.success('Task updated');
      onSaved?.();
      reset(data);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed');
    }
  };

  return (
    <Card>
      <div className="font-semibold mb-3 flex items-center gap-2"><Save size={16} /> Update progress</div>
      <form onSubmit={handleSubmit(onSubmit)} className="grid sm:grid-cols-2 gap-3">
        <div>
          <label className="label">Status</label>
          <select className="input" {...register('status')}>
            <option value="pending">Pending</option>
            <option value="in-progress">In progress</option>
            <option value="on-hold">On hold</option>
            <option value="completed">Completed</option>
          </select>
        </div>
        <div>
          <label className="label">Priority</label>
          <select className="input" {...register('priority')}>
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
          </select>
        </div>
        <div>
          <label className="label">Completion %</label>
          <input type="number" min={0} max={100} className="input" {...register('completionPercent', { valueAsNumber: true })} />
        </div>
        <div>
          <label className="label">Deadline</label>
          <input type="date" className="input" {...register('deadline')} />
        </div>
        <div className="sm:col-span-2 flex justify-end">
          <button type="submit" disabled={isSubmitting || !isDirty} className="btn-primary">
            <Save size={14} /> {isSubmitting ? 'Saving...' : 'Save changes'}
          </button>
        </div>
      </form>
    </Card>
  );
}

function CommentsPanel({ task, onChanged }) {
  const { user } = useAuth();
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [replyTo, setReplyTo] = useState(null); // the comment being replied to

  const comments = task.comments || [];
  const byId = useMemo(() => {
    const m = {};
    comments.forEach((c) => { m[String(c._id)] = c; });
    return m;
  }, [comments]);

  const send = async () => {
    const value = text.trim();
    if (!value) return;
    setSending(true);
    try {
      await taskService.addComment(task._id, {
        text: value,
        replyTo: replyTo?._id || undefined,
      });
      setText('');
      setReplyTo(null);
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed');
    } finally {
      setSending(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Escape' && replyTo) {
      e.preventDefault();
      setReplyTo(null);
      return;
    }
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <Card>
      <div className="font-semibold mb-3 flex items-center gap-2"><MessageSquare size={16} /> Comments ({comments.length})</div>
      <div className="space-y-3 max-h-96 overflow-y-auto">
        {comments.length === 0 && (
          <div className="text-sm text-slate-500 italic py-2">Be the first to comment</div>
        )}
        {comments.map((c) => {
          const own = c.user?._id === user?._id;
          const parent = c.replyTo ? byId[String(c.replyTo)] : null;
          return (
            <div key={c._id} className="group flex items-start gap-2.5">
              <div className={`w-8 h-8 shrink-0 rounded-full grid place-items-center text-white text-xs font-semibold ${
                own ? 'bg-gradient-to-br from-brand-500 to-indigo-600' : 'bg-gradient-to-br from-slate-500 to-slate-700'
              }`}>
                {c.user?.name?.[0]?.toUpperCase() || '?'}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-medium">{c.user?.name || 'Unknown'}</span>
                  <span className="text-[11px] text-slate-400">{fmtDateTime(c.createdAt)}</span>
                </div>
                {parent && (
                  <div className="mt-1 flex items-start gap-1 px-2 py-1 rounded-md bg-slate-50 dark:bg-slate-800/70 border-l-2 border-slate-300 dark:border-slate-700 text-xs text-slate-500">
                    <CornerDownRight size={10} className="mt-0.5 shrink-0" />
                    <div className="min-w-0">
                      <span className="font-medium text-slate-600 dark:text-slate-300">{parent.user?.name || 'Unknown'}</span>
                      <span className="text-slate-400 dark:text-slate-500"> · </span>
                      <span className="truncate">{(parent.text || '').length > 90 ? `${(parent.text || '').slice(0, 87)}...` : (parent.text || '')}</span>
                    </div>
                  </div>
                )}
                <div className="text-sm text-slate-700 dark:text-slate-200 whitespace-pre-wrap mt-0.5">{c.text}</div>
                <button
                  onClick={() => setReplyTo(c)}
                  className="opacity-0 group-hover:opacity-100 transition mt-1 inline-flex items-center gap-1 text-[11px] font-medium text-slate-500 hover:text-brand-600"
                >
                  <Reply size={11} /> Reply
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {replyTo && (
        <div className="mt-3 flex items-start justify-between gap-2 px-3 py-2 rounded-lg bg-brand-50 dark:bg-brand-500/10 border-l-4 border-brand-500">
          <div className="min-w-0">
            <div className="text-[11px] font-medium text-brand-700 dark:text-brand-300 flex items-center gap-1">
              <Reply size={11} /> Replying to {replyTo.user?.name || 'Unknown'}
            </div>
            <div className="text-xs text-slate-600 dark:text-slate-300 truncate mt-0.5">{replyTo.text}</div>
          </div>
          <button type="button" onClick={() => setReplyTo(null)} className="text-slate-500 hover:text-rose-500 p-1">
            <X size={14} />
          </button>
        </div>
      )}

      <div className="mt-3 flex items-end gap-2 border-t border-slate-200 dark:border-slate-800 pt-3">
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder={replyTo ? `Reply to ${replyTo.user?.name || ''}...` : 'Write a comment... (Enter to send)'}
          rows={1}
          className="input resize-none flex-1 max-h-32"
        />
        <button onClick={send} disabled={sending || !text.trim()} className="btn-primary">
          <Send size={14} /> {sending ? 'Sending...' : 'Send'}
        </button>
      </div>
    </Card>
  );
}

function DeleteTaskModal({ task, onClose, onDeleted }) {
  const [confirmText, setConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);

  const doDelete = async () => {
    setDeleting(true);
    try {
      await taskService.remove(task._id);
      toast.success('Task deleted');
      onDeleted?.();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title="Delete task"
      size="md"
      footer={
        <>
          <button onClick={onClose} className="btn-secondary">Cancel</button>
          <button onClick={doDelete} className="btn-danger" disabled={deleting || confirmText !== task.title}>
            <Trash2 size={14} /> {deleting ? 'Deleting...' : 'Delete task'}
          </button>
        </>
      }
    >
      <div className="space-y-3">
        <div className="flex items-start gap-3 p-3 rounded-lg bg-rose-50 dark:bg-rose-500/10 border border-rose-200 dark:border-rose-500/30">
          <AlertTriangle className="text-rose-600 shrink-0 mt-0.5" size={18} />
          <div className="text-sm text-rose-700 dark:text-rose-300">
            This will permanently delete <b>{task.title}</b>. This action cannot be undone.
          </div>
        </div>
        <div>
          <label className="label">Type <span className="font-mono text-rose-600">{task.title}</span> to confirm</label>
          <input className="input" value={confirmText} onChange={(e) => setConfirmText(e.target.value)} autoFocus />
        </div>
      </div>
    </Modal>
  );
}
